import { Button } from '../../../core-components/Button';
import { SkeletonTextLines, Skeleton } from '../../../core-components/Skeleton';
import { UniversalCarousel, CarouselStage, CarouselDefaultPagination } from '../../shared/UniversalCarousel';
import { DashboardTravelItineraryIntroductionProgressBar } from './components/ProgressBar';
import type { ButtonProps } from '../../../core-components/Button';
import type { ReactNode } from 'react';
export type DashboardTravelItineraryIntroductionProps = {
  fallback?: boolean;
  pages?: ReactNode[];
  button?: ButtonProps;
};

/**
 * The `DashboardTravelItineraryIntroduction` component displays an introduction section for a travel itinerary dashboard, including a carousel of pages, progress bar, and action button.
 *
 * ## Props
 *
 * - `fallback`: Optional boolean to render fallback UI.
 * - `pages`: Optional array of ReactNode elements representing the carousel pages.
 * - `button`: Optional ButtonProps for the main action button.
 *
 * ```tsx
 * <DashboardTravelItineraryIntroduction
 *   fallback={false}
 *   pages={[<div>Page 1</div>, <div>Page 2</div>]}
 *   button={{ children: 'Continue', onClick: () => {} }}
 * />
 * ```
 */
export const DashboardTravelItineraryIntroduction = ({
  fallback,
  pages,
  button
}: DashboardTravelItineraryIntroductionProps) => {
  const totalPages = pages?.length ?? 1;
  return <div className="text-lg text-marine-high-emphasis" data-sentry-component="DashboardTravelItineraryIntroduction" data-sentry-source-file="DashboardTravelItineraryIntroduction.tsx">
      <UniversalCarousel data-sentry-element="UniversalCarousel" data-sentry-source-file="DashboardTravelItineraryIntroduction.tsx">
        {!fallback && <DashboardTravelItineraryIntroductionProgressBar totalPages={totalPages} />}
        <CarouselStage itemProps={{
        className: 'h-fit'
      }} data-sentry-element="CarouselStage" data-sentry-source-file="DashboardTravelItineraryIntroduction.tsx">
          {fallback ? <div>
              <div className="mt-8">
                <Skeleton className="mb-6 h-8" />
                <SkeletonTextLines number={10} />
              </div>
              <div className="mt-8 flex flex-col-reverse justify-center gap-8 md:mt-10 md:flex-row md:justify-between">
                <Button fallback />
                <CarouselDefaultPagination className="mt-0 self-center md:self-auto" centered />
              </div>
            </div> : pages?.map((page, index) => <div key={index}>
                {page}
                <div className="mt-8 flex flex-col-reverse justify-center gap-8 md:mt-10 md:flex-row md:justify-between">
                  {button ? <Button on="white" variant="primary" {...button} /> : null}
                  <CarouselDefaultPagination className="mt-0 self-center md:self-auto" centered />
                </div>
              </div>)}
        </CarouselStage>
      </UniversalCarousel>
    </div>;
};